import { FormikCheckbox, FormikTextInput, Spinner } from '@td-client-frontend/common';
import { TDHLogo } from '@td-client-frontend/icons';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLocalStorage } from 'react-use';
import * as Yup from 'yup';
import { PiwikAction, PiwikCategory, PiwikName } from '../@types/common';
import { trackEvent } from '../api/piwik/piwikService';
import { Button } from '../components/form-controls/Button';
import { useAuth } from '../context/AuthContext';
import { useBannerContext } from '../context/BannerContext';
import { useLoginBannerMutation } from '../hooks/useLoginBanner';
import { isMobileSafari, isSafari } from 'react-device-detect';

interface FormLoginValues {
  username: string;
  password: string;
  rememberUsername: boolean;
}

export default function Login() {
  const [value, setValue, remove] = useLocalStorage<string>('rememberUsername');
  const authContext = useAuth();
  const { getShowBanner } = useLoginBannerMutation();
  const { setShowBanner } = useBannerContext();

  const addTrackEvent = (name: PiwikName) => {
    trackEvent({
      category: PiwikCategory.BUTTONS,
      action: PiwikAction.CLICKED,
      name
    });
  };

  const handleSubmit = async ({ username, password, rememberUsername }: FormLoginValues) => {
    rememberUsername ? setValue(username) : remove();
    try {
      const login: boolean = await authContext.login(username, password);
      if (login) {
        try {
          const { showBanner }: { showBanner: boolean } = await getShowBanner();
          setShowBanner(showBanner);
        } catch (e) {
          console.error('An error occurred while getting the login banner info', e);
        }
      }
      addTrackEvent(PiwikName.LOGIN);
    } catch (error) {
      toast.warn(error.response?.data?.error || error.message, {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  const SafariWarningBanner = () => (
    <div
      style={{ backgroundColor: '#FFD60A' }}
      className="flex flex-col items-center absolute top-0 left-0 h-20 w-full px-16 py-3 text-lg"
    >
      <div className="text-left">
        <p>The Safari browser you are using is not supported and will require a setting change to function properly.</p>
        <p>
          Adjust browser setting: <b>Preferences → Privacy → (</b>uncheck
          <b>) Prevent cross-site tracking</b>
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col justify-center w-full h-full max-w-md mx-auto font-effra">
      {(isSafari || isMobileSafari) && <SafariWarningBanner />}

      <Formik
        initialValues={
          {
            username: value,
            password: '',
            rememberUsername: value && value.length > 1
          } as FormLoginValues
        }
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
        validationSchema={Yup.object({
          username: Yup.string().required('Required'),
          password: Yup.string().required('Required')
        })}
        onSubmit={(values, { setSubmitting }: FormikHelpers<FormLoginValues>) => {
          setSubmitting(true);
          handleSubmit(values).finally(() => setSubmitting(false));
        }}
      >
        {({ isSubmitting }) => {
          return (
            <Form className="px-4 pt-6 pb-8 md:px-8" data-testid="loginForm">
              <div className="my-4">
                <TDHLogo />
              </div>
              <h1 className="mb-6">Login to your account</h1>

              <FormikTextInput
                data-testid="emailInput"
                label="Username"
                name="username"
                type="text"
                placeholder="Please enter username"
              />
              <FormikTextInput label="Password" name="password" type="password" placeholder="Please enter password" />
              <div className="flex items-center justify-between mt-2">
                <FormikCheckbox name="rememberUsername">Remember username</FormikCheckbox>
                <Link to="/forgot-password">Forgot your password?</Link>
              </div>
              <div className="flex flex-col my-4 space-y-8">
                <Button
                  disabled={isSubmitting}
                  type="submit"
                  data-testid="submitButton"
                  className="flex justify-center"
                >
                  {isSubmitting ? <Spinner /> : 'Login'}
                </Button>
                <span className="space-y-2 text-xs leading-tight text-justify text-dark-gray">
                  <p>
                    All content of this system and its associated sub-systems are PROPRIETARY INFORMATION and remain the
                    sole and exclusive property of this company. This system may be accessed and used by authorized
                    personnel only. Authorized users may only perform authorized activities and may not exceed the
                    limits of such authorization. Disclosure of information found in this system for any unauthorized
                    use is *STRICTLY PROHIBITED*. All activities on this system are subject to monitoring. Intentional
                    misuse of this system can result in disciplinary action or criminal prosecution.
                  </p>
                  <p>
                    Your continued use of this portal indicates your agreement to the following policies:
                    <a
                      href="https://teladochealth.com/privacy-policy/#7.-cookies-and-information-used-for-advertising"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 mx-1"
                    >
                      Web Cookie Policy
                    </a>
                    ,
                    <a
                      href="https://teladochealth.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 mx-1"
                    >
                      Web Privacy Policy
                    </a>
                    and
                    <a
                      href="https://teladochealth.com/terms-and-conditions/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 ml-1"
                    >
                      Terms and Conditions
                    </a>
                    .
                  </p>
                </span>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
